// Pure conflict-detection logic for the bulk-add review list. Conflicts are
// recomputed from the current word list on every render, so resolutions (merge,
// keep-both, exclude) need only mutate the words / resolved set — the derived
// conflict view follows automatically and converges as words are removed.
import type { WordTag } from '@/schemas/word';
import { normalizeTitle } from './parse';
import type { BulkWord, DbMatch } from './types';

/**
 * A pairwise conflict between two candidates with the same normalized title:
 * - `batch` — two words from the pasted batch.
 * - `db`    — one batch word and a pre-existing database word (read-only).
 * `key` uniquely identifies the pair so it can be marked resolved.
 */
export type BulkConflict =
  | { kind: 'batch'; key: string; a: BulkWord; b: BulkWord }
  | { kind: 'db'; key: string; word: BulkWord; dbWord: DbMatch };

/** Stable key for a batch-vs-batch pair (order-independent). */
export function batchPairKey(idA: string, idB: string): string {
  return [idA, idB].sort().join('::');
}

/** Stable key for a batch-vs-DB pair. */
export function dbPairKey(wordId: string, dbId: string): string {
  return `${wordId}::db::${dbId}`;
}

/**
 * Merge two tag lists into one, de-duplicating by label and preferring the first
 * occurrence (so an existing noyau colour on the surviving tag is kept).
 */
export function unionTags(a: WordTag[], b: WordTag[]): WordTag[] {
  const seen = new Set<string>();
  const merged: WordTag[] = [];
  for (const tag of [...a, ...b]) {
    if (seen.has(tag.label)) continue;
    seen.add(tag.label);
    merged.push(tag);
  }
  return merged;
}

/**
 * Compute every unresolved pairwise conflict in the current word list.
 *
 * Only `create` words that are not crossed out participate: excluding a word or
 * resolving its DB collision via "Fusionner" (op → mergeInto) drops it from
 * detection. Batch-vs-batch pairs are all unordered pairs sharing a normalized
 * title; batch-vs-DB pairs come from each word's `dbMatch`. Pairs whose key is in
 * `resolved` ("Garder les deux") are skipped.
 */
export function detectConflicts(words: BulkWord[], resolved: ReadonlySet<string>): BulkConflict[] {
  const active = words.filter((w) => !w.excluded && w.op.kind === 'create');
  const conflicts: BulkConflict[] = [];

  // Batch-vs-batch: group by normalized title, emit every unordered pair.
  const groups = new Map<string, BulkWord[]>();
  for (const word of active) {
    const key = normalizeTitle(word.title);
    const group = groups.get(key);
    if (group) group.push(word);
    else groups.set(key, [word]);
  }
  for (const group of groups.values()) {
    for (let i = 0; i < group.length; i++) {
      for (let j = i + 1; j < group.length; j++) {
        const a = group[i];
        const b = group[j];
        if (!a || !b) continue;
        const key = batchPairKey(a.id, b.id);
        if (!resolved.has(key)) conflicts.push({ kind: 'batch', key, a, b });
      }
    }
  }

  // Batch-vs-DB: one conflict per still-creating word that matches a DB word.
  for (const word of active) {
    if (!word.dbMatch) continue;
    const key = dbPairKey(word.id, word.dbMatch.id);
    if (!resolved.has(key)) conflicts.push({ kind: 'db', key, word, dbWord: word.dbMatch });
  }

  return conflicts;
}

/** Whether a given word is part of at least one unresolved conflict. */
export function isInConflict(wordId: string, conflicts: BulkConflict[]): boolean {
  return conflicts.some((c) =>
    c.kind === 'batch' ? c.a.id === wordId || c.b.id === wordId : c.word.id === wordId,
  );
}

/** The first unresolved conflict involving a word, used to open its dialog. */
export function firstConflictFor(
  wordId: string,
  conflicts: BulkConflict[],
): BulkConflict | undefined {
  return conflicts.find((c) =>
    c.kind === 'batch' ? c.a.id === wordId || c.b.id === wordId : c.word.id === wordId,
  );
}
